import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, ViewChild } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationEnd, Router } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs/operators';
import packageJson from '../../package.json';
import { AuthUser } from './models/auth-user.model';
import { AmplitudeService } from './services/amplitude.service';
import { ApiService } from './services/api.service';
import { AuthService } from './services/auth.service';
import { LocalService } from './services/local.service';
import { SidenavService } from './services/sidenav.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent {
  @ViewChild(MatSidenav)
  sidenav!: MatSidenav;

  isLogged: boolean = false;
  title = 'World Cup 2022';
  user: AuthUser | null = null;
  isAdmin: boolean = false;
  isImpersonating: boolean = false;
  version: string = packageJson.version;

  constructor(
    private observer: BreakpointObserver,
    private authService: AuthService,
    private sidenavService: SidenavService,
    private apiService: ApiService,
    private localService: LocalService,
    private router: Router,
    private amplitudeService: AmplitudeService,
    private swUpdate: SwUpdate,
    private snackBar: MatSnackBar,
  ) {
    this.initializeApp();
    this.initAuth();
    this.initSwUpdateListener();
  }

  ngOnInit() {}

  ngAfterViewInit() {
    this.sidenavService.setSidenav(this.sidenav);
    this.observer
      .observe(['(max-width: 800px)'])
      .subscribe(this.checkMenuPosition);
  }

  private initSwUpdateListener() {
    if (!this.swUpdate.isEnabled) return;

    this.swUpdate.versionUpdates
      .pipe(filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'))
      .subscribe(() => this.promptUpdate());
  }

  private promptUpdate() {
    const snack = this.snackBar.open(
      '¡Nueva versión disponible!',
      'Actualizar',
      { duration: 0, panelClass: 'update-snackbar' },
    );
    snack.onAction().subscribe(() => window.location.reload());
  }

  private async initializeApp() {
    this.isLogged = false;
    this.amplitudeService.initializeAPI();
    this.initRouterSubscription();
    this.startAuthListener();
  }

  private startAuthListener() {
    this.authService.changed.subscribe(async (authenticated: boolean) => {
      this.isLogged = authenticated;
      this.user = await this.authService.getUserAsync();
      this.isAdmin = this.authService.isAdmin();
      this.isImpersonating = this.authService.isImpersonating();

      if (!this.isLogged) {
        this.sidenav?.close();
        return;
      }

      this.checkMenuPosition(window.matchMedia('(max-width: 800px)'));
      this.amplitudeService.setUser();
    });
  }

  private async initAuth() {
    const isAuthUrl =
      window.location.href.indexOf('/login') === -1 &&
      window.location.href.indexOf('/register') === -1;

    if (!isAuthUrl) {
      return;
    }

    const token = await this.localService.getDataV2<string>('token');
    this.apiService.setToken(token || '');

    this.authService
      .checkValidToken()
      .then(() => {
        this.isLogged = true;
      })
      .catch(() => {
        this.isLogged = false;
      });
  }

  private initRouterSubscription() {
    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        this.amplitudeService.logEvent('Navigation', { page: event.url });
      }
    });
  }

  goToPage(path: string) {
    this.router.navigate([path], {
      replaceUrl: true,
    });

    this.checkMenuPosition(window.matchMedia('(max-width: 800px)'));
  }

  async stopImpersonation(): Promise<void> {
    const restored = await this.authService.stopImpersonation();
    if (restored) {
      this.router.navigate(['/admin/users'], { replaceUrl: true });
    }
  }

  private checkMenuPosition(res: any) {
    if (!this.isLogged) {
      return;
    }

    if (res.matches) {
      this.sidenav.mode = 'over';
      this.sidenav.close();
    } else {
      this.sidenav.mode = 'side';
      this.sidenav.open();
    }
  }

  /**
   * Handle file from browsing
   */
  fileBrowseHandler(event: any) {
    if (!this.user?._id) {
      return;
    }

    const files = event.target.files;

    if (files.length === 1 && files[0].type.indexOf('image/') === 0) {
      const fileObj = files[0];

      this.apiService
        .uploadFile(`/users/upload-avatar/${this.user._id}`, fileObj)
        .then((user) => {
          this.user = user as AuthUser;
        });
    }
  }
}
